<template>
    <p-modal
        name="exportSalesByDccModal"
        label="Export Sales By DCC"
        ref="exportSalesByDccModal"
        size="lg"
        @ok.prevent="handleModalOk"
    >
     <template v-slot:default>
            <p-form ref="salesByDccForm">
                <div class="container-fluid content-wrapper">
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="POS Devices" class="static-height">
                                <p-filter-select
                                    data-type="posDevicesWithLocation"
                                    rules="required"
                                    :default-selected="true"
                                    :initial-display-count="10"
                                    v-model="reportParameters.registerIds"
                                />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                            <p-card title="Additional Options">
                                <b-row>
                                    <b-col>
                                         <p-checkbox size="md"
                                        label="DCC Details by Device"
                                        v-model="
                                    reportParameters.dccDetailsByDevice
                                " />
                            <p-checkbox size="md"
                                        label="DCC Summary for Selected Devices"
                                        v-model="
                                    reportParameters.dccSummaryForSelectedDevices
                                " />
                                    </b-col>
                                </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2"
                        >OK</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Sales By DCC',
            reportParameters: {
                registerIds: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                dccDetailsByDevice:false,
                dccSummaryForSelectedDevices:false,
                locationId: null
            }
        };
    },
    computed: {
        pageTitle() {
            return 'Sales By Dcc';
        }
    },
    methods: {
        async show() {
            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            this.reportParameters.registerIds = (posDevices || []).map(x => x.value);
            this.reportParameters.dccDetailsByDevice = true;
            this.reportParameters.dccSummaryForSelectedDevices = false;
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.exportSalesByDccModal.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The POS device ids are the only saved
        // values tied to changing data — a disabled or deleted register drops out of the list. The
        // dates and the two option checkboxes are literal values, not references.
        async validateReportParameters(savedParameters) {
            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation') ?? [];

            return (savedParameters.registerIds ?? []).every(registerId =>
                posDevices.some(posDevice => posDevice.value == registerId));
        },
        handleModalOk: async function() {

            if (!(await this.$refs.salesByDccForm.validate())) {
                return;
            }

            if (this.reportParameters.registerIds.length === 0) {
                this.$appError(
                    'Please select one or more Pos Devices.'
                );
                return;
            }

            if (!this.reportParameters.dccDetailsByDevice && !this.reportParameters.dccSummaryForSelectedDevices) {
                this.$appError(
                    'Please select one or more Additional Options.'
                );
                return;
            }

            this.isBusy = true;

            // The current location always wins: a restored template carries the location that was
            // active when it was saved, which would otherwise run the report for the wrong one.
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            await axios
                .post('/sales/exportSalesByDcc', this.reportParameters, {
                    responseType: 'blob'
                })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'Sales By Dcc',
                        'application/pdf'
                    );
                    this.$appSuccess(`Sales By DCC generated successfully.`)
                    this.$refs.exportSalesByDccModal.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    }
};
</script>

<style scoped>
.static-height {
    height:370px
}
</style>
